/**
 * Dropdown de notificações ancorado no sino da topbar.
 *
 * Estado: closed (só badge) ↔ open (lista das últimas 30).
 * Click numa notificação: marca como lida + navega pro linkUrl.
 * Botão "Marcar todas como lidas" no topo.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Bell,
  CheckCircle2,
  AlertTriangle,
  Eye,
  Sparkles,
  AlertCircle,
} from "lucide-react";
import { useCompanyId } from "@/lib/CompanyContext";
import {
  notificationsApi,
  type Notification,
  type NotificationKind,
} from "@/api/notifications";

const KIND_META: Record<
  NotificationKind,
  { icon: typeof Bell; color: string; bg: string }
> = {
  task_review: {
    icon: Eye,
    color: "var(--nd-blue)",
    bg: "rgba(6,62,110,0.10)",
  },
  task_blocked: {
    icon: AlertTriangle,
    color: "var(--nd-orange-pun)",
    bg: "rgba(204,51,68,0.10)",
  },
  task_done: {
    icon: CheckCircle2,
    color: "#21a86d",
    bg: "rgba(33,168,109,0.10)",
  },
  routine_created_task: {
    icon: Sparkles,
    color: "var(--nd-orange)",
    bg: "rgba(255,112,0,0.10)",
  },
  budget_warning: {
    icon: AlertCircle,
    color: "var(--nd-orange-pun)",
    bg: "rgba(204,51,68,0.10)",
  },
  system: {
    icon: Bell,
    color: "var(--wf-ink-2)",
    bg: "rgba(128,128,128,0.10)",
  },
};

function formatRelative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return "agora";
  if (ms < 3_600_000) return `há ${Math.round(ms / 60_000)}min`;
  if (ms < 86_400_000) return `há ${Math.round(ms / 3_600_000)}h`;
  return `há ${Math.round(ms / 86_400_000)}d`;
}

export function NotificationsDropdown() {
  const companyId = useCompanyId();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const unreadCount = useQuery({
    queryKey: ["notifications", companyId, "unread-count"],
    queryFn: () => notificationsApi.unreadCount(companyId!),
    enabled: !!companyId,
    refetchInterval: 30_000,
  });

  const list = useQuery({
    queryKey: ["notifications", companyId, "list"],
    queryFn: () => notificationsApi.list(companyId!, { limit: 30 }),
    enabled: !!companyId && open,
    staleTime: 10_000,
  });

  const markRead = useMutation({
    mutationFn: (id: string) => notificationsApi.markRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications", companyId] });
    },
  });

  const markAllRead = useMutation({
    mutationFn: () => notificationsApi.markAllRead(companyId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications", companyId] });
    },
  });

  // Click fora fecha o dropdown
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const count = unreadCount.data?.count ?? 0;
  const items = list.data ?? [];

  const onClickNotification = (n: Notification) => {
    if (!n.readAt) markRead.mutate(n.id);
    setOpen(false);
    if (n.linkUrl) navigate(n.linkUrl);
  };

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="wf-btn wf-btn--ghost wf-btn--sm"
        style={{
          padding: "4px 8px",
          display: "flex",
          alignItems: "center",
          gap: 6,
          position: "relative",
        }}
        aria-label="Notificações"
      >
        <Bell
          size={13}
          color="var(--nd-orange)"
          className={count > 0 && !open ? "wf-bell-ring" : undefined}
        />
        {count > 0 && (
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              padding: "1px 6px",
              borderRadius: 999,
              background: "var(--nd-orange)",
              color: "#fff",
              minWidth: 16,
              textAlign: "center",
            }}
          >
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            right: 0,
            width: 380,
            maxHeight: 480,
            background: "var(--wf-paper)",
            border: "1px solid var(--wf-ink-5)",
            borderRadius: 12,
            boxShadow: "0 8px 24px rgba(0,0,0,0.10)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            zIndex: 100,
          }}
        >
          <header
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "10px 14px",
              borderBottom: "1px solid var(--wf-ink-5)",
            }}
          >
            <div className="wf-display" style={{ fontSize: 14, fontWeight: 700 }}>
              Notificações
            </div>
            {count > 0 && (
              <button
                type="button"
                onClick={() => markAllRead.mutate()}
                disabled={markAllRead.isPending}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: 11,
                  color: "var(--nd-orange-pun)",
                  fontWeight: 600,
                  fontFamily: "var(--wf-font-body)",
                }}
              >
                Marcar todas como lidas
              </button>
            )}
          </header>

          <div style={{ overflowY: "auto", flex: 1 }}>
            {list.isLoading && (
              <div
                style={{
                  padding: "20px 14px",
                  fontSize: 12,
                  color: "var(--wf-ink-3)",
                  textAlign: "center",
                }}
              >
                carregando…
              </div>
            )}
            {!list.isLoading && items.length === 0 && (
              <div
                style={{
                  padding: "32px 14px",
                  textAlign: "center",
                  color: "var(--wf-ink-3)",
                  fontSize: 12,
                }}
              >
                Nenhuma notificação ainda.
              </div>
            )}
            {items.map((n) => {
              const meta = KIND_META[n.kind] ?? KIND_META.system;
              const Icon = meta.icon;
              const isUnread = !n.readAt;
              return (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => onClickNotification(n)}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 10,
                    padding: "12px 14px",
                    background: isUnread ? "rgba(255,112,0,0.04)" : "transparent",
                    border: "none",
                    borderBottom: "1px solid var(--wf-ink-5)",
                    cursor: "pointer",
                    textAlign: "left",
                    fontFamily: "var(--wf-font-body)",
                    transition: "background 120ms",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = "rgba(255,112,0,0.08)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = isUnread
                      ? "rgba(255,112,0,0.04)"
                      : "transparent")
                  }
                >
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      flexShrink: 0,
                      borderRadius: 8,
                      background: meta.bg,
                      color: meta.color,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Icon size={15} strokeWidth={1.75} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: isUnread ? 700 : 500,
                        color: "var(--wf-ink)",
                        marginBottom: 2,
                        lineHeight: 1.3,
                      }}
                    >
                      {n.title}
                    </div>
                    {n.body && (
                      <div
                        style={{
                          fontSize: 12,
                          color: "var(--wf-ink-2)",
                          lineHeight: 1.4,
                          marginBottom: 4,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {n.body}
                      </div>
                    )}
                    <div
                      style={{
                        fontSize: 10,
                        color: "var(--wf-ink-3)",
                      }}
                    >
                      {formatRelative(n.createdAt)}
                    </div>
                  </div>
                  {isUnread && (
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: 999,
                        background: "var(--nd-orange)",
                        flexShrink: 0,
                        marginTop: 4,
                      }}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
